import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';
import lodashOmit from 'lodash/fp/omit';

import isBoolean from 'common/util/isBoolean';
import setNullEmptyValues from 'common/util/setNullEmptyValues';
import {
  getHouseholdApplicationDataFields,
  omitSensitiveFields,
} from 'common/householdApplication/applicationHelpers';
import { getListingFullAddressString } from 'common/listingHelpers';

import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
  virtualFormFieldIds,
} from 'common/householdApplication/applicationFormFields';
import {
  RENTAL_APPLICATION_STEP_PAY,
  RENTAL_APPLICATION_STEP_REQUIREMENTS,
  RENTAL_APPLICATION_STEP_FORM,
} from 'common/householdApplication/rentalApplicationHelpers';
import {
  SR_API_ACTION_RENTER_PROFILE_UPDATE_CREDIT_AND_BACKGROUND_DATA,
  SR_API_ACTION_CHECK_LOGIN_EXIST,
} from 'api/actionTypes';

import {
  scrollToFormField,
  getValidFormValues,
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrText from 'client/components/SrText';
import SrSeparator from 'client/components/SrSeparator';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFormFieldGroup from 'client/components/SrFormFieldGroup';
import SrRentalApplicationFlowStepHeader from 'client/components/SrRentalApplicationFlowStepHeader';
import SrRentalApplicationFlowTestPersonalInfo from 'client/components/SrRentalApplicationFlowTest/SrRentalApplicationFlowTestPersonalInfo';
import SrModalConfirmNoSsn from 'client/components/SrModalConfirmNoSsn/SrModalConfirmNoSsn';

import withApiState from 'client/helpers/withApiState';
import postWithState from 'client/actions/postWithState';
import HouseholdApplicationActions from 'client/actions/HouseholdApplication';
import RenterProfileActions from 'client/actions/RenterProfile';

const omitVirtualFormValues = lodashOmit(virtualFormFieldIds);

@withApiState({
  updateApplicationApiState: {
    action: SR_API_ACTION_RENTER_PROFILE_UPDATE_CREDIT_AND_BACKGROUND_DATA,
  },
})
class SrRentalApplicationFlowStepPersonalInfo extends Component {
  static propTypes = {
    updateApplicationApiState: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    renterProfile: PropTypes.object.isRequired,
    agreeRenterRequirements: PropTypes.bool,
    goToNextRentalApplicationStep: PropTypes.func.isRequired,
    exitRentalApplication: PropTypes.func.isRequired,
    householdApplicationId: PropTypes.number,
    guarantorApplicationId: PropTypes.number,
  };

  constructor() {
    super();

    this.state = {
      checkLoginExistResults: {},
      userHasSsn: true,
      displayConfirmNoSsnModal: false,
    };
  }

  _validateEmail = (email) => {
    const { user, dispatch } = this.props;
    const { checkLoginExistResults } = this.state;
    const isSameCurrentUserEmail = user.email === email;

    if (!email) {
      return;
    }

    return new Promise((resolve, reject) => {
      // eslint-disable-next-line prefer-promise-reject-errors
      const rejectPromise = () => reject('This email is already used by another account.');
      if (isSameCurrentUserEmail) {
        resolve();
        return;
      }
      if (isBoolean(checkLoginExistResults[email])) {
        if (checkLoginExistResults[email]) {
          rejectPromise();
        } else {
          resolve();
        }
        return;
      }

      dispatch(postWithState, {
        action: SR_API_ACTION_CHECK_LOGIN_EXIST,
        payload: {
          email,
        },
        onSuccess: ({ exists }) => {
          this.setState({
            checkLoginExistResults: {
              ...checkLoginExistResults,
              [email]: exists,
            },
          });
          if (exists) {
            rejectPromise();
          } else {
            resolve();
          }
        },
      });
    });
  };

  _onValidateForm = () => {
    const formikBag = this.formik.getFormikBag();
    const formValues = formikBag.values;
    const formFields = getApplicationFormFields({
      formType: APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
      formValues: formValues,
    });
    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFielIdsWithErrors = Object.keys(formErrors);

    if (!formFielIdsWithErrors.length) {
      formikBag.handleSubmit();
    } else {
      formFielIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFielIdsWithErrors[0],
      });
    }
  };

  _onSubmitForm = (values) => {
    const {
      goToNextRentalApplicationStep,
      householdApplicationId,
      guarantorApplicationId,
      dispatch,
      user,
      listing,
    } = this.props;
    const personaInfo = {
      ...omitVirtualFormValues(setNullEmptyValues(values)),
      currentAddress: getListingFullAddressString({
        addressStreetDisplay: `${values.currentAddressStreetName} ${
          values.currentAddressApartmentUnit || ''
        }`.trim(), // eslint-disable-line max-len
        addressCity: values.currentAddressCity,
        addressStateCode: values.currentAddressStateCode,
        addressZip: values.currentAddressZip,
      }),
    };

    dispatch(RenterProfileActions.updateCreditAndBackgroundData, {
      userId: user.id,
      applicationId: householdApplicationId || guarantorApplicationId,
      updates: personaInfo,
    })
      .then(() => {
        return dispatch(HouseholdApplicationActions.shouldGoToCheckout, {
          householdApplicationId: householdApplicationId || guarantorApplicationId,
          listingId: listing.id,
        });
      })
      .then((shouldGoToCheckout) => {
        goToNextRentalApplicationStep(
          shouldGoToCheckout ? RENTAL_APPLICATION_STEP_PAY : RENTAL_APPLICATION_STEP_FORM,
        );
      });
  };

  _onSaveAndExit = () => {
    const {
      dispatch,
      user,
      householdApplicationId,
      guarantorApplicationId,
      exitRentalApplication,
    } = this.props;

    const validFormValues = omitVirtualFormValues(
      setNullEmptyValues(
        getValidFormValues({
          formFields: getApplicationFormFields({
            formType: APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
            formValues: this._parseFormValues(this.formik.state.values),
          }),
          values: this.formik.state.values,
        }),
      ),
    );

    if (Object.keys(validFormValues)) {
      dispatch(RenterProfileActions.updateCreditAndBackgroundData, {
        userId: user.id,
        applicationId: householdApplicationId || guarantorApplicationId,
        updates: validFormValues,
      });
    }

    exitRentalApplication();
  };

  _parseFormValues = (values) => {
    const { userHasSsn } = this.state;
    return { ...values, noSsn: !userHasSsn };
  };

  _onSsnOptionClick = ({ formikBag }) => {
    if (this.state.userHasSsn) {
      return this.setState({
        displayConfirmNoSsnModal: true,
      });
    }
    formikBag.setFieldValue('noSsn', false);
    return this.setState({
      userHasSsn: true,
    });
  };

  _handleNoSsn = ({ formikBag }) => {
    formikBag.setFieldValue('ssn', '');
    formikBag.setFieldValue('confirmSsn', '');
    formikBag.setFieldValue('noSsn', true);

    formikBag.setFieldTouched('ssn', false);
    formikBag.setFieldTouched('confirmSsn', false);
    formikBag.setFieldTouched('noSsn', false);

    this.setState({
      userHasSsn: false,
      displayConfirmNoSsnModal: false,
    });
  };

  render() {
    const {
      updateApplicationApiState,
      renterProfile,
      agreeRenterRequirements,
      getCurrentRentalApplicationUrl,
      user,
      householdApplicationId,
      guarantorApplicationId,
    } = this.props;

    const { userHasSsn, displayConfirmNoSsnModal } = this.state;

    if (!agreeRenterRequirements) {
      return <Redirect to={getCurrentRentalApplicationUrl(RENTAL_APPLICATION_STEP_REQUIREMENTS)} />;
    }

    const defaultValues = omitSensitiveFields(
      this._parseFormValues(getHouseholdApplicationDataFields(renterProfile)),
    );

    return (
      <Formik
        ref={(formik) => {
          this.formik = formik;
        }}
        validateOnBlur
        validateOnChange={false}
        validate={(values) => {
          const formFields = getApplicationFormFields({
            formType: APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
            formValues: this._parseFormValues(values),
          });
          return createFormValidationFn(formFields)(values);
        }}
        initialValues={createInitialValues({
          formFields: getApplicationFormFields({
            formType: APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
            formValues: defaultValues,
          }),
          defaultValues,
        })}
        onSubmit={this._onSubmitForm}
        render={(formikBag) => {
          const formFields = getApplicationFormFields({
            formType: APPLICATION_FORM_TYPE_SR_APPLICATION_CREDIT_INFO,
            formValues: this._parseFormValues(formikBag.values),
            setFieldValue: formikBag.setFieldValue,
            validateEmail: this._validateEmail,
          });
          const getFormFieldsById = createFormFieldsByIdGetter(formFields);

          return (
            <>
              <div className="RentalApplicationPage">
                <SrRentalApplicationFlowStepHeader
                  activeStep={1}
                  onSaveAndExit={this._onSaveAndExit}
                  isGuarantorApp={guarantorApplicationId && !householdApplicationId}
                />
                <div className="main">
                  <div className="mainContent">
                    <SrText className="pageHeadline" size="2xl" weight="medium" leading="2xl">
                      Provide info for a credit & background check
                    </SrText>
                    <SrSeparator />
                    <Form>
                      <SrFormFieldGroup
                        title="personal"
                        formFields={getFormFieldsById([
                          'firstName',
                          'middleName',
                          'lastName',
                          'nameSuffix',
                          'email',
                          'phone',
                          'dateOfBirth',
                          'ssn',
                          'confirmSsn',
                        ])}
                        formikBag={formikBag}
                      />
                      <SrBox marginTop="8px">
                        <SrText
                          onClick={() => this._onSsnOptionClick({ formikBag })}
                          cursor="pointer"
                          color="blue300"
                        >
                          {userHasSsn ? 'Don’t have a SSN/ITIN?' : 'Apply with SSN/ITIN'}
                        </SrText>
                      </SrBox>
                      <SrFormFieldGroup
                        title="current address"
                        formFields={getFormFieldsById([
                          'currentAddressStreetName',
                          'currentAddressApartmentUnit',
                          'currentAddressCity',
                          'currentAddressStateCode',
                          'currentAddressZip',
                        ])}
                        formikBag={formikBag}
                      />
                    </Form>
                    <SrStaticFooter size="135">
                      <ButtonDeprecated
                        disabled={updateApplicationApiState.inProgress}
                        onClick={this._onValidateForm}
                      >
                        {updateApplicationApiState.inProgress
                          ? 'Loading...'
                          : user.stripeCustomerId
                          ? 'continue application'
                          : 'next: pay app fee'}
                      </ButtonDeprecated>
                    </SrStaticFooter>
                  </div>
                </div>
                <SrRentalApplicationFlowTestPersonalInfo formikBag={formikBag} />
              </div>
              {displayConfirmNoSsnModal && (
                <SrModalConfirmNoSsn
                  onClose={() => this.setState({ displayConfirmNoSsnModal: false })}
                  onContinue={() => this._handleNoSsn({ formikBag })}
                />
              )}
            </>
          );
        }}
      />
    );
  }
}

SrRentalApplicationFlowStepPersonalInfo.displayName = 'SrRentalApplicationFlowStepPersonalInfo';

export default SrRentalApplicationFlowStepPersonalInfo;
